import { formatPropertyAddress } from '../utils/propertyAddress';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import MlsDisclosure from './MlsDisclosure';

const propertyTypeLabels = {
  'Single Family Residence': 'Casa unifamiliar',
  Condominium: 'Condominio',
  Townhouse: 'Casa adosada',
  Residential: 'Residencial',
  Land: 'Terreno'
};

const getAddress = formatPropertyAddress;

const getBathrooms = (property) => {
  const reported = Number(property.bathrooms_total_integer ?? property.bathrooms_total);
  if (Number.isFinite(reported) && reported > 0) return reported;
  const full = Number(property.bathrooms_full || 0);
  const half = Number(property.bathrooms_half || 0);
  const calculated = full + (half * 0.5);
  return calculated || null;
};

export default function FeaturedProperties() {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const officeKeys = useMemo(() => SITE_CONFIG.AGENT.OFFICE_KEYS?.filter(Boolean) || [], []);
  const [properties, setProperties] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [status, setStatus] = useState(officeKeys.length ? 'loading' : 'empty');

  useEffect(() => {
    if (!officeKeys.length) return undefined;
    const controller = new AbortController();

    const loadFeaturedProperties = async () => {
      setStatus('loading');
      try {
        const response = await fetch('/featured-properties.json', { cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error(`Featured properties request failed: ${response.status}`);
        const data = await response.json();
        const uniqueListings = Array.from(
          new Map((data.properties || [])
            .filter((property) => property.listing_key && property.first_photo_url)
            .map((property) => [property.listing_key, property])).values()
        ).slice(0, 10);
        setProperties(uniqueListings);
        setActiveIndex(0);
        setStatus(uniqueListings.length ? 'ready' : 'empty');
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching brokerage featured properties:', error);
          setStatus('error');
        }
      }
    };

    loadFeaturedProperties();
    return () => controller.abort();
  }, [officeKeys]);

  const activeProperty = properties[activeIndex];
  const goToProperty = (nextIndex) => {
    if (!properties.length) return;
    setActiveIndex((nextIndex + properties.length) % properties.length);
  };

  const formatPrice = (price) => new Intl.NumberFormat(isSpanish ? 'es-US' : 'en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(Number(price) || 0);

  const copy = isSpanish ? {
    kicker: 'HOUSEITGOING LLC · Inventario activo',
    title: 'Propiedades destacadas de la correduría.',
    body: 'Explore propiedades actuales representadas por HOUSEITGOING LLC y seleccione cualquiera para ver todos los detalles del listado.',
    loading: 'Cargando propiedades destacadas…',
    empty: 'Próximamente habrá nuevas propiedades destacadas de HOUSEITGOING LLC.',
    error: 'Las propiedades destacadas no están disponibles en este momento.',
    view: 'Ver propiedad',
    previous: 'Propiedad anterior',
    next: 'Propiedad siguiente',
    beds: 'hab.',
    baths: 'baños',
    listedBy: 'Presentada por HOUSEITGOING LLC',
    select: 'Seleccionar'
  } : {
    kicker: 'HOUSEITGOING LLC · Active inventory',
    title: 'Featured brokerage properties.',
    body: 'Explore current properties represented by HOUSEITGOING LLC and select any residence to view the complete listing details.',
    loading: 'Loading featured properties…',
    empty: 'New HOUSEITGOING LLC featured properties are coming soon.',
    error: 'Featured properties are unavailable right now.',
    view: 'View residence',
    previous: 'Previous property',
    next: 'Next property',
    beds: 'beds',
    baths: 'baths',
    listedBy: 'Presented by HOUSEITGOING LLC',
    select: 'Select'
  };

  return (
    <section className="gt-featured gt-section" aria-labelledby="gt-featured-title">
      <header className="gt-featured-heading">
        <div>
          <span className="gt-kicker">{copy.kicker}</span>
          <h2 id="gt-featured-title">{copy.title}</h2>
        </div>
        <p>{copy.body}</p>
      </header>

      {status === 'loading' && (
        <div className="gt-featured-loading" role="status">
          <span />
          <div><i /><i /><i /></div>
          <p>{copy.loading}</p>
        </div>
      )}

      {(status === 'empty' || status === 'error') && (
        <div className="gt-featured-empty" role="status">
          <span>HIG</span>
          <p>{status === 'error' ? copy.error : copy.empty}</p>
        </div>
      )}

      {status === 'ready' && activeProperty && (
        <div className="gt-featured-showcase">
          <Link className="gt-featured-primary" to={`/listing/${activeProperty.listing_key}`} aria-label={`${copy.view}: ${getAddress(activeProperty)}`}>
            <img src={activeProperty.first_photo_url} alt={getAddress(activeProperty)} fetchPriority="high" />
            <span className="gt-featured-image-shade" />
            <span className="gt-featured-counter">{String(activeIndex + 1).padStart(2, '0')} / {String(properties.length).padStart(2, '0')}</span>
            <div className="gt-featured-property-copy">
              <span>{copy.listedBy}</span>
              <strong>{formatPrice(activeProperty.current_price || activeProperty.price)}</strong>
              <h3>{getAddress(activeProperty)}</h3>
              <div>
                {(activeProperty.property_sub_type || activeProperty.property_type) && (
                  <em>{isSpanish ? propertyTypeLabels[activeProperty.property_sub_type || activeProperty.property_type] || activeProperty.property_sub_type || activeProperty.property_type : activeProperty.property_sub_type || activeProperty.property_type}</em>
                )}
                {activeProperty.bedrooms_total != null && <em>{activeProperty.bedrooms_total} {copy.beds}</em>}
                {getBathrooms(activeProperty) != null && <em>{getBathrooms(activeProperty)} {copy.baths}</em>}
              </div>
              <b>{copy.view} <span>↗</span></b>
            </div>
          </Link>

          <aside className="gt-featured-controls" aria-label={copy.title}>
            <div className="gt-featured-nav">
              <button type="button" onClick={() => goToProperty(activeIndex - 1)} aria-label={copy.previous}>←</button>
              <span><i style={{ width: `${((activeIndex + 1) / properties.length) * 100}%` }} /></span>
              <button type="button" onClick={() => goToProperty(activeIndex + 1)} aria-label={copy.next}>→</button>
            </div>
            <div className="gt-featured-thumbnails">
              {properties.map((property, index) => (
                <button
                  type="button"
                  className={index === activeIndex ? 'is-active' : ''}
                  key={property.listing_key}
                  onClick={() => setActiveIndex(index)}
                  aria-label={`${copy.select}: ${getAddress(property)}`}
                  aria-pressed={index === activeIndex}
                >
                  <img src={property.first_photo_url} alt="" loading={index < 3 ? 'eager' : 'lazy'} />
                  <span><small>{String(index + 1).padStart(2, '0')}</small><strong>{property.city}</strong></span>
                </button>
              ))}
            </div>
          </aside>
        </div>
      )}

      {status === 'ready' && activeProperty && (
        <div className="gt-featured-disclosure">
          <img src={SITE_CONFIG.MLS.LOGO_URL} alt={SITE_CONFIG.MLS.SHORT_NAME} />
          <span><MlsDisclosure variant="preview" showLicenses /></span>
        </div>
      )}
    </section>
  );
}
